import { useState } from "react";
import type { Account } from "@retirement-planner/contract";
import { useLanguage } from "../i18n/LanguageProvider";
import {
  useAssumptions,
  useCreateAccount,
  useDashboard,
  useDeleteAccount,
  useUpdateAccount,
} from "../hooks/api";
import { OnTrackHeadline } from "../components/OnTrackHeadline";
import { ProjectionChart } from "../components/charts";
import { AccountForm } from "../components/AccountForm";
import { Button, Panel, StatePanel } from "../components/primitives";
import { formatMoney } from "../lib/format";

export function DashboardScreen() {
  const { t, locale } = useLanguage();
  const dash = useDashboard();
  const assumptions = useAssumptions(); // parallel — for the real plan-to-age in the headline label
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);

  if (dash.isLoading) return <main id="main-content" className="app-main"><StatePanel kind="loading" /></main>;
  if (dash.isError || !dash.data) return <main id="main-content" className="app-main"><StatePanel kind="error" onRetry={() => dash.refetch()} /></main>;

  const d = dash.data;
  const currency = d.netWorth.currency;
  const empty = d.accounts.length === 0;
  const planToAge = assumptions.data?.planToAge ?? 90;

  return (
    <main id="main-content" className="app-main">
      <OnTrackHeadline
        onTrack={d.onTrack}
        probability={d.probabilityOfSuccess}
        projectedRetirementAge={d.projectedRetirementAge}
        planToAge={planToAge}
      />
      <p className="tabular" style={{ fontSize: "var(--font-size-h2)" }}>
        {t("netWorth")}: {formatMoney(d.netWorth.amountMinorUnits, currency, locale)}
      </p>

      <div className="dashboard-grid">
        <Panel title={t("accounts")}>
          {empty ? (
            <StatePanel kind="empty" message={t("emptyAccounts")}>
              <Button variant="primary" onClick={() => setAddOpen(true)}>{t("addAccount")}</Button>
            </StatePanel>
          ) : (
            <>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {d.accounts.map((a) => (
                  <li key={a.accountId} style={{ borderBottom: "1px solid var(--color-border-subtle)" }}>
                    <button
                      type="button"
                      onClick={() => setEditing(a)}
                      aria-label={`${t("edit")}: ${a.name}`}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                        width: "100%", minHeight: 44, padding: "8px 4px", background: "none", border: 0,
                        font: "inherit", color: "inherit", cursor: "pointer", textAlign: "left",
                      }}
                    >
                      <span>{a.name}</span>
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="tabular">{formatMoney(a.value.amountMinorUnits, a.value.currency, locale)}</span>
                        <span aria-hidden="true" style={{ color: "var(--color-text-secondary)" }}>✎</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <Button variant="secondary" onClick={() => setAddOpen(true)} style={{ marginTop: 16 }}>
                + {t("addAccount")}
              </Button>
            </>
          )}
        </Panel>

        <Panel title={`${t("savingsProjection")} (${t("real")})`}>
          {d.projectionSummary && d.projectionSummary.length > 0 ? (
            <ProjectionChart series={d.projectionSummary} label={t("savingsProjection")} />
          ) : (
            <StatePanel kind="empty" message={t("setAssumptions")} />
          )}
        </Panel>
      </div>

      <AccountForm
        open={addOpen}
        mode="add"
        currency={currency}
        saving={createAccount.isPending}
        onClose={() => setAddOpen(false)}
        onSave={(input) => createAccount.mutate(input, { onSuccess: () => setAddOpen(false) })}
      />

      {editing && (
        <AccountForm
          open
          mode="edit"
          currency={currency}
          account={editing}
          saving={updateAccount.isPending || deleteAccount.isPending}
          onClose={() => setEditing(null)}
          onSave={(input) =>
            updateAccount.mutate(
              { id: editing.accountId, body: input },
              { onSuccess: () => setEditing(null) },
            )
          }
          onDelete={() =>
            deleteAccount.mutate(editing.accountId, { onSuccess: () => setEditing(null) })
          }
        />
      )}
    </main>
  );
}
